import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Badge, Button, Navbar, Nav, Modal, Form, Alert } from "react-bootstrap";
import { Formik } from 'formik';
import 'bootstrap/dist/css/bootstrap.min.css';
import "./app.css";

const App = () => {
   const [selectedEvent, setSelectedEvent] = useState(null);
   const [selectedMedia, setSelectedMedia] = useState(null);
   const [showMediaModal, setShowMediaModal] = useState(false);
   const [user, setUser] = useState(null);
   const [isAuthenticated, setIsAuthenticated] = useState(false);

   // Media files from assets/images/pics
   const mediaFiles = [
      // TO ADD YOUR IMAGES:
      // 1. Put your images/videos in /Users/joshmilstead/Code/tiki/assets/images/pics/
      // 2. Add entries here following the format:
      // { name: "example.jpg", type: "image", path: "/assets/images/pics/example.jpg" },
      // { name: "example.mp4", type: "video", path: "/assets/images/pics/example.mp4" },
      // 3. Run 'npm run build:webapp' to rebuild
      // 4. Refresh the browser to see your gallery
   ];

   const handleMediaClick = (media) => {
      setSelectedMedia(media);
      setShowMediaModal(true);
   };

   // Sample events data - you can replace this with real data
   const upcomingEvents = [
      // Add your events here when ready
   ];

   const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-US', {
         year: 'numeric',
         month: 'long',
         day: 'numeric'
      });
   };

   // Check authentication status on component mount
   useEffect(() => {
      const checkAuthStatus = async () => {
         try {
            const response = await fetch('https://onedeadbird.com/api/auth/status', {
               method: 'GET',
               credentials: 'include',
               headers: {
                  'Content-Type': 'application/json',
               },
            });
            
            if (response.ok) {
               const data = await response.json();
               if (data.success && data.isAuthenticated) {
                  setIsAuthenticated(true);
                  setUser(data.user);
               }
            }
         } catch (error) {
            console.log('Auth check failed:', error);
            // Fail silently - user just won't see the talk button
         }
      };

      checkAuthStatus();
   }, []);

   return (
      <div className="appcontainer">
         <Navbar bg="dark" variant="dark" expand="lg" fixed="top" className="px-3">
            <Navbar.Brand href="#home" className="fw-bold">Kaipo the Tiki God</Navbar.Brand>
            <Navbar.Toggle aria-controls="basic-navbar-nav" />
            <Navbar.Collapse id="basic-navbar-nav">
               <Nav className="ms-auto">
                  <Nav.Link href="#about">About</Nav.Link>
                  <Nav.Link href="#gallery">Gallery</Nav.Link>
                  <Nav.Link href="#events">Events</Nav.Link>
                  <Nav.Link href="#contact">Contact</Nav.Link>
               </Nav>
            </Navbar.Collapse>
         </Navbar>

         <Container fluid="lg">
            {/* Hero Section */}
            <Row className="text-center mb-5 py-5">
               <Col>
                  <h1 className="display-3 mb-4 fw-bold">Kaipo the Tiki God</h1>
                  <p className="lead fs-4 mb-4">Interactive AI-Powered Tiki Bar Experience</p>
                  <div className="mb-4">
                     <Badge bg="success" className="fs-6 px-3 py-2 me-2">Conversational AI</Badge>
                     <Badge bg="info" className="fs-6 px-3 py-2 me-2">Voice Synthesis</Badge>
                     <Badge bg="warning" className="fs-6 px-3 py-2">Animated Face</Badge>
                  </div>
                  {isAuthenticated && (
                     <div className="mt-4">
                        <Button 
                           variant="primary" 
                           size="lg" 
                           href="/view?direct=true"
                           className="px-4 py-2"
                        >
                           🗣️ Talk to Kaipo in Your Browser
                        </Button>
                        <p className="text-light mt-2 small">Welcome back, {user?.name}!</p>
                     </div>
                  )}
               </Col>
            </Row>

            {/* Concept Section */}
            <section id="about" className="mb-5">
               <Row>
                  <Col lg={10} className="mx-auto">
                     <Card bg="dark" text="light" className="border-0 section-card">
                        <Card.Body className="p-4">
                           <h2 className="text-center mb-4 section-title">Meet Kaipo</h2>
                           <p className="fs-5 mb-4 text-center">
                              <strong>Kaipo</strong> is a life-sized tiki god who lives in his own tiki bar installation. This funny and happy character loves to tell jokes, 
                              sing songs (especially "Lime in the Coconut"), and share tiki culture wisdom with visitors through natural AI-powered conversations.
                           </p>
                           
                           <Row className="g-4 mt-4">
                              <Col md={6}>
                                 <div className="feature-box text-center">
                                    <h4 className="mb-3">🗣️ Tiki Bar Conversations</h4>
                                    <p>Kaipo loves chatting with visitors about tiki culture, telling uplifting stories, and making the best jokes. If he doesn't know something, he'll suggest you buy a drink!</p>
                                 </div>
                              </Col>
                              <Col md={6}>
                                 <div className="feature-box text-center">
                                    <h4 className="mb-3">🎵 Musical Performances</h4>
                                    <p>He loves singing popular songs but always gets the words wrong in tiki-related ways. The one song he knows perfectly? "Lime in the Coconut!"</p>
                                 </div>
                              </Col>
                              <Col md={6}>
                                 <div className="feature-box text-center">
                                    <h4 className="mb-3">🌿 Sacred Carvings</h4>
                                    <p>Kaipo is carved with meaningful symbols: leaves (nature), water drops (ocean love), music notes (singing), fire (tiki culture), and drinks (party time!).</p>
                                 </div>
                              </Col>
                              <Col md={6}>
                                 <div className="feature-box text-center">
                                    <h4 className="mb-3">📺 Animated Expressions</h4>
                                    <p>His screen face comes alive with animated expressions and mouth movements that sync perfectly with his voice, bringing this foam-carved tiki god to life.</p>
                                 </div>
                              </Col>
                           </Row>
                        </Card.Body>
                     </Card>
                  </Col>
               </Row>
            </section>

            {/* Technology Section */}
            <section className="mb-5">
               <Row>
                  <Col lg={10} className="mx-auto">
                     <Card bg="dark" text="light" className="border-0 section-card">
                        <Card.Body className="p-4">
                           <h2 className="text-center mb-4 section-title">Technology & Craftsmanship</h2>
                           
                           <Row className="g-4">
                              <Col md={4}>
                                 <div className="feature-box text-center">
                                    <h5 className="mb-3">🗿 Foam Sculpture</h5>
                                    <p>Hand-carved from foam into a life-sized tiki god with intricate details and a beautiful leaf crown, featuring an integrated screen face.</p>
                                 </div>
                              </Col>
                              <Col md={4}>
                                 <div className="feature-box text-center">
                                    <h5 className="mb-3">🎭 Personality AI</h5>
                                    <p>Advanced AI gives Kaipo his unique personality - funny, uplifting, and knowledgeable about tiki culture, with a tendency to recommend drinks!</p>
                                 </div>
                              </Col>
                              <Col md={4}>
                                 <div className="feature-box text-center">
                                    <h5 className="mb-3">🎙️ Voice Synthesis</h5>
                                    <p>ElevenLabs technology provides realistic voice generation with emotional inflection that matches Kaipo's cheerful personality.</p>
                                 </div>
                              </Col>
                           </Row>
                        </Card.Body>
                     </Card>
                  </Col>
               </Row>
            </section>

            {/* Gallery Section */}
            <section id="gallery" className="mb-5">
               <Row>
                  <Col>
                     <h2 className="text-center mb-4 section-title">Gallery</h2>
                     <p className="text-center text-light mb-5">
                        Behind the scenes photos and videos from the creation and exhibition of "Kaipo the Tiki God"
                     </p>
                  </Col>
               </Row>
               
               {mediaFiles.length > 0 ? (
                  <Row className="g-3 justify-content-center">
                     {mediaFiles.map((media, index) => (
                        <Col xl={3} lg={4} md={6} sm={6} xs={12} key={index} className="d-flex">
                           <Card 
                              bg="dark" 
                              text="light" 
                              className="media-card border-0 w-100 h-100" 
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleMediaClick(media)}
                           >
                              <div className="media-thumbnail">
                                 {media.type === 'image' ? (
                                    <Card.Img 
                                       variant="top" 
                                       src={media.path} 
                                       alt={media.name}
                                       style={{ height: '200px', objectFit: 'cover' }}
                                    />
                                 ) : (
                                    <div className="video-thumbnail">
                                       <video 
                                          src={media.path}
                                          style={{ height: '200px', objectFit: 'cover' }}
                                          muted
                                       />
                                       <div className="video-overlay">
                                          <div className="play-button">▶</div>
                                       </div>
                                    </div>
                                 )}
                              </div>
                              <Card.Body className="p-2">
                                 <Card.Text className="small text-light mb-0">
                                    {media.type === 'video' ? '🎥 Video' : '📷 Photo'}
                                 </Card.Text>
                              </Card.Body>
                           </Card>
                        </Col>
                     ))}
                  </Row>
               ) : (
                  <Row>
                     <Col className="text-center">
                        <Card bg="dark" text="light" className="border-0 section-card">
                           <Card.Body className="p-5">
                              <h4>Gallery Coming Soon</h4>
                              <p className="text-light">Photos and videos will be added here to showcase Kaipo the Tiki God experience!</p>
                           </Card.Body>
                        </Card>
                     </Col>
                  </Row>
               )}
            </section>

            {/* Events Section */}
            <section id="events" className="mb-5">
               <Row>
                  <Col>
                     <h2 className="text-center mb-4 section-title">Upcoming Exhibitions</h2>
                     <p className="text-center text-light mb-5">
                        Find out where you can experience "Kaipo the Tiki God" next
                     </p>
                  </Col>
               </Row>

               {upcomingEvents.length > 0 ? (
                  <Row className="g-4">
                     {upcomingEvents.map((event) => (
                        <Col lg={4} md={6} className="mb-4" key={event.id}>
                           <Card 
                              bg="dark" 
                              text="light" 
                              className="h-100 event-card border-0"
                              onClick={() => setSelectedEvent(event)}
                           >
                              <Card.Body className="d-flex flex-column">
                                 <div className="mb-3">
                                    <Badge bg="success" className="mb-2">Upcoming</Badge>
                                    <Card.Title className="h5">{event.title}</Card.Title>
                                 </div>
                                 
                                 <div className="mb-3">
                                    <p className="mb-1"><strong>📍 {event.venue}</strong></p>
                                    <p className="text-light mb-1">{event.location}</p>
                                    <p className="text-primary mb-0">
                                       {formatDate(event.date)} - {formatDate(event.endDate)}
                                    </p>
                                 </div>
                                 
                                 <p className="flex-grow-1">{event.description}</p>
                                 
                                 <Button variant="outline-primary" size="sm" className="mt-auto">
                                    Learn More
                                 </Button>
                              </Card.Body>
                           </Card>
                        </Col>
                     ))}
                  </Row>
               ) : (
                  <Row>
                     <Col className="text-center">
                        <Card bg="dark" text="light" className="border-0 section-card">
                           <Card.Body className="p-5">
                              <h4>No Upcoming Events</h4>
                              <p className="text-light">Check back soon for new exhibition announcements!</p>
                           </Card.Body>
                        </Card>
                     </Col>
                  </Row>
               )}
            </section>

            {/* Contact Section */}
            <section id="contact" className="mb-5">
               <Row>
                  <Col lg={8} className="mx-auto">
                     <Card bg="dark" text="light" className="border-0 section-card">
                        <Card.Body className="p-4">
                           <h2 className="text-center mb-4 section-title">Host Kaipo the Tiki God</h2>
                           <p className="text-center mb-4">
                              Interested in bringing Kaipo to your gallery, museum, festival, or event? 
                              This unique AI-powered tiki god creates memorable conversations, tells great jokes, and shares authentic tiki culture with visitors.
                           </p>
                           
                           <Formik
                              initialValues={{
                                 organization: '',
                                 email: '',
                                 subject: '',
                                 message: ''
                              }}
                              validate={values => {
                                 const errors = {};
                                 if (!values.organization) {
                                    errors.organization = 'Organization name is required';
                                 }
                                 if (!values.email) {
                                    errors.email = 'Email is required';
                                 } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
                                    errors.email = 'Invalid email address';
                                 }
                                 if (!values.subject) {
                                    errors.subject = 'Subject is required';
                                 }
                                 if (!values.message) {
                                    errors.message = 'Message is required';
                                 }
                                 return errors;
                              }}
                              onSubmit={(values, { setSubmitting, setStatus, resetForm }) => {
                                 fetch('/sendMessage', {
                                    method: 'POST',
                                    headers: {
                                       'Content-Type': 'application/json',
                                    },
                                    body: JSON.stringify({
                                       sender: values.email,
                                       subject: values.subject,
                                       content: `Organization: ${values.organization}\nEmail: ${values.email}\n\nMessage:\n${values.message}`
                                    }),
                                 })
                                 .then(response => response.json())
                                 .then(data => {
                                    if (data.status === 'success') {
                                       setStatus({ type: 'success', message: 'Message sent successfully! We\'ll get back to you soon.' });
                                       resetForm();
                                    } else {
                                       setStatus({ type: 'error', message: 'Failed to send message. Please try again.' });
                                    }
                                    setSubmitting(false);
                                 })
                                 .catch(error => {
                                    console.error('Error:', error);
                                    setStatus({ type: 'error', message: 'Failed to send message. Please try again.' });
                                    setSubmitting(false);
                                 });
                              }}
                           >
                              {({
                                 values,
                                 errors,
                                 touched,
                                 handleChange,
                                 handleBlur,
                                 handleSubmit,
                                 isSubmitting,
                                 status
                              }) => (
                                 <Form onSubmit={handleSubmit}>
                                    {status && (
                                       <Alert variant={status.type === 'success' ? 'success' : 'danger'} className="mb-4">
                                          {status.message}
                                       </Alert>
                                    )}
                                    
                                    <Row className="g-3">
                                       <Col md={6}>
                                          <Form.Group>
                                             <Form.Label>Organization/Venue Name</Form.Label>
                                             <Form.Control
                                                type="text"
                                                name="organization"
                                                placeholder="Your gallery, museum, or event name"
                                                value={values.organization}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                isInvalid={touched.organization && errors.organization}
                                                className="bg-dark text-light border-secondary"
                                             />
                                             <Form.Control.Feedback type="invalid">
                                                {errors.organization}
                                             </Form.Control.Feedback>
                                          </Form.Group>
                                       </Col>
                                       <Col md={6}>
                                          <Form.Group>
                                             <Form.Label>Email Address</Form.Label>
                                             <Form.Control
                                                type="email"
                                                name="email"
                                                placeholder="your.email@example.com"
                                                value={values.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                isInvalid={touched.email && errors.email}
                                                className="bg-dark text-light border-secondary"
                                             />
                                             <Form.Control.Feedback type="invalid">
                                                {errors.email}
                                             </Form.Control.Feedback>
                                          </Form.Group>
                                       </Col>
                                    </Row>
                                    
                                    <Row className="g-3 mt-2">
                                       <Col>
                                          <Form.Group>
                                             <Form.Label>Subject</Form.Label>
                                             <Form.Control
                                                type="text"
                                                name="subject"
                                                placeholder="Exhibition inquiry for Kaipo the Tiki God"
                                                value={values.subject}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                isInvalid={touched.subject && errors.subject}
                                                className="bg-dark text-light border-secondary"
                                             />
                                             <Form.Control.Feedback type="invalid">
                                                {errors.subject}
                                             </Form.Control.Feedback>
                                          </Form.Group>
                                       </Col>
                                    </Row>
                                    
                                    <Row className="g-3 mt-2">
                                       <Col>
                                          <Form.Group>
                                             <Form.Label>Message</Form.Label>
                                             <Form.Control
                                                as="textarea"
                                                rows={4}
                                                name="message"
                                                placeholder="Tell me about your venue, expected visitor numbers, event dates, and any specific requirements for hosting Kaipo..."
                                                value={values.message}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                isInvalid={touched.message && errors.message}
                                                className="bg-dark text-light border-secondary"
                                             />
                                             <Form.Control.Feedback type="invalid">
                                                {errors.message}
                                             </Form.Control.Feedback>
                                          </Form.Group>
                                       </Col>
                                    </Row>
                                    
                                    <div className="text-center mt-4">
                                       <Button 
                                          variant="primary" 
                                          type="submit" 
                                          disabled={isSubmitting}
                                          size="lg"
                                       >
                                          {isSubmitting ? 'Sending...' : 'Send Inquiry'}
                                       </Button>
                                    </div>
                                 </Form>
                              )}
                           </Formik>
                        </Card.Body>
                     </Card>
                  </Col>
               </Row>
            </section>

            {/* Footer */}
            <Row className="mt-5 pt-4 border-top border-secondary">
               <Col className="text-center">
                  <p className="text-light">
                     "Kaipo the Tiki God" is an interactive AI sculpture that can only be experienced in person.
                     <br />
                     Come chat with Kaipo, hear his jokes, and learn about tiki culture!
                  </p>
               </Col>
            </Row>
         </Container>

         {/* Media Modal */}
         <Modal 
            show={showMediaModal} 
            onHide={() => setShowMediaModal(false)} 
            size="lg" 
            centered
            className="media-modal"
         >
            <Modal.Header closeButton className="bg-dark text-light border-secondary">
               <Modal.Title>{selectedMedia?.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-dark p-0">
               <div className="modal-media-container">
                  {selectedMedia?.type === 'image' ? (
                     <img 
                        src={selectedMedia.path} 
                        alt={selectedMedia.name}
                        className="img-fluid w-100"
                        style={{ maxHeight: '70vh', objectFit: 'contain' }}
                     />
                  ) : selectedMedia?.type === 'video' ? (
                     <video 
                        src={selectedMedia.path}
                        controls
                        autoPlay
                        className="w-100"
                        style={{ maxHeight: '70vh' }}
                     >
                        Your browser does not support the video tag.
                     </video>
                  ) : null}
               </div>
            </Modal.Body>
         </Modal>
      </div>
   );
};

export default App;
